import { Auth0User, Roles } from '../types'

/**
 * Dual-role (student / advisor) support.
 *
 * A dual-role user holds both the student role and an advisor/admin role in
 * their token, but the app only ever activates ONE persona at a time. The
 * active persona is chosen here and persisted per identity in localStorage.
 */

/** The persona a dual-role user is currently operating as. */
export type ActiveRole = 'student' | 'advisor'

/** adminSetting.features flag gating the dual-role experience per institution. */
export const DUAL_ROLE_SWITCHER_FEATURE = 'DUAL_ROLE_SWITCHER'

/** Persona used when a dual-role user has no stored choice (first login / after logout). */
export const DEFAULT_ACTIVE_ROLE: ActiveRole = 'student'

const STORAGE_PREFIX = 'civitas.activeRole.'

const ROLES_CLAIM = 'https://api.student.civitaslearning.com/roles'

/** localStorage key holding the persisted persona for a given identity. */
export const getActiveRoleStorageKey = (sub: string): string => `${STORAGE_PREFIX}${sub}`

/** Parse a raw storage value into an ActiveRole, or null when invalid. */
export const parseActiveRole = (value: string | null | undefined): ActiveRole | null =>
  value === 'student' || value === 'advisor' ? value : null

export const getStoredActiveRole = (sub: string): ActiveRole | null => {
  try {
    return parseActiveRole(localStorage.getItem(getActiveRoleStorageKey(sub)))
  } catch {
    return null
  }
}

export const storeActiveRole = (sub: string, role: ActiveRole): void => {
  try {
    localStorage.setItem(getActiveRoleStorageKey(sub), role)
  } catch {
    // Storage unavailable (private mode / quota) — the choice just won't persist.
  }
}

/**
 * A dual-role user holds the student role plus an advisor/admin role.
 * Civitas employees are excluded — they bypass role/permission checks entirely.
 */
export const isDualRoleUser = (roles: Roles[] = []): boolean =>
  roles.includes(Roles.student) &&
  (roles.includes(Roles.advisor) || roles.includes(Roles.admin)) &&
  !roles.includes(Roles.civitasEmployee)

/**
 * Derive the persona-scoped user handed to the app. In student mode the user
 * is exactly a student (admin permissions stripped); in advisor mode the
 * student role is dropped and `advisor` is normalized to imply `admin` so all
 * existing role guards keep working. Single-role users pass through untouched.
 */
export function deriveEffectiveUser(raw: Auth0User, active: ActiveRole | null): Auth0User {
  if (!active || !isDualRoleUser(raw.roles)) {
    return raw
  }
  if (active === 'student') {
    return { ...raw, roles: [Roles.student], [ROLES_CLAIM]: [Roles.student], permissions: [] }
  }
  const advisorRoles = (raw.roles ?? []).filter((role) => role !== Roles.student)
  const roles = advisorRoles.includes(Roles.admin) ? advisorRoles : [...advisorRoles, Roles.admin]
  return { ...raw, roles, [ROLES_CLAIM]: roles }
}
